import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { Loader2, FileBarChart, BusFront } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { StatCard } from "@/components/Ui";
import PrintButtons from "@/components/PrintButtons";
import { Crest, ThermalBrand } from "@/components/Brand";
import { useStations } from "@/components/SearchForm";
import { useAuth, userRoles } from "@/context/AuthContext";
import { api, errMsg } from "@/lib/api";
import { money, fmtDate, fmtTime } from "@/lib/format";

const daysAgo = (n) => { const d = new Date(); d.setDate(d.getDate() - n); return d.toISOString().slice(0, 10); };

// One trip's "Trip Summary" (From -> To passenger & amount split), matching the station payout slip.
function SegmentTable({ segments }) {
  const pax = segments.reduce((s, x) => s + x.pax, 0);
  const amt = segments.reduce((s, x) => s + x.amount, 0);
  return (
    <table className="w-full text-left text-sm" data-testid="trip-segment-table">
      <thead><tr className="text-xs uppercase tracking-wide text-slate-500"><th className="py-1">From → To</th><th className="text-right">Pax</th><th className="text-right">Fare</th><th className="text-right">Amount</th></tr></thead>
      <tbody>
        {segments.map((s, i) => (
          <tr key={i} className="border-t border-slate-100">
            <td className="py-1.5">{s.from_city} → {s.to_city}</td>
            <td className="text-right">{s.pax}</td>
            <td className="text-right">{money(s.fare)}</td>
            <td className="text-right font-semibold">{money(s.amount)}</td>
          </tr>
        ))}
      </tbody>
      <tfoot><tr className="border-t-2 border-slate-200 font-bold"><td className="py-1.5">Total Pax {pax}</td><td className="text-right">{pax}</td><td /><td className="text-right text-[#0052FF]">{money(amt)}</td></tr></tfoot>
    </table>
  );
}

function TripCard({ r }) {
  return (
    <div className="card-soft space-y-3 p-4" data-testid={`trip-report-card-${r.trip_id}-${r.station_id}`}>
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div>
          <div className="font-display text-lg font-extrabold text-[#182678]">{r.route}</div>
          <div className="text-xs text-slate-500">{fmtDate(r.departure)} {fmtTime(r.departure)} · Boarding at {r.station_city}</div>
        </div>
        <div className="flex flex-wrap gap-1.5 text-xs">
          <span className="chip bg-slate-100 text-slate-600">Bus {r.bus_number || "—"}</span>
          <span className="chip bg-slate-100 text-slate-600">{r.driver_name || "Driver —"}{r.driver_phone ? ` · ${r.driver_phone}` : ""}</span>
          <span className="chip bg-slate-100 text-slate-600">Capacity {r.capacity}</span>
        </div>
      </div>
      <div className="grid grid-cols-2 gap-2 sm:grid-cols-4">
        {[["Seats sold", r.seats_sold], ["Booking amount", money(r.booking_amount)], ["Platform charges", money(r.platform)], ["Tracking charges", money(r.tracking)],
          ["Station Master amount", money(r.sm_amount)], [`Station charges (${r.station_charge_pct}%)`, money(r.station_charge)], ["Driver amount", money(r.driver_amount)]].map(([k, v]) => (
          <div key={k} className="rounded-xl bg-slate-50 p-2.5">
            <div className="text-[11px] font-semibold uppercase tracking-wide text-slate-500">{k}</div>
            <div className="font-display text-base font-extrabold text-slate-800">{v}</div>
          </div>
        ))}
      </div>
      <div className="rounded-xl border border-slate-100 p-3">
        <div className="label-xs mb-1">Trip Summary</div>
        <SegmentTable segments={r.segments} />
      </div>
    </div>
  );
}

function ReportDoc({ rows, totals, label, from, to, format }) {
  const thermal = format === "thermal";
  return (
    <div className={thermal ? "thermal" : "a4 text-sm"} data-testid="trip-report-print">
      <div className="center text-center">
        {thermal ? <ThermalBrand /> : (<><Crest className="mx-auto h-14 w-14" /><div className="big font-display text-lg font-extrabold text-[#182678]">GPRTU INTERCITY</div></>)}
        <div className="big mt-1 font-bold">STATION TRIP REPORT</div>
        <div>{label}</div>
        <div className="text-xs">{from} → {to}</div>
      </div>
      <hr className="my-2 border-dashed border-slate-300" />
      {rows.map((r) => (
        <div key={`${r.trip_id}-${r.station_id}`} className="mb-3">
          <div className="font-bold">{r.route} · {fmtDate(r.departure)} {fmtTime(r.departure)}</div>
          <div className="text-xs">Bus {r.bus_number || "—"} · {r.driver_name || "Driver —"} · Boarding {r.station_city} · Seats {r.seats_sold}</div>
          <table className="w-full text-left text-xs">
            <tbody>
              {r.segments.map((s, i) => (
                <tr key={i}><td>{s.from_city} → {s.to_city}</td><td className="text-right">{s.pax} pax</td><td className="text-right">@{money(s.fare)}</td><td className="text-right">{money(s.amount)}</td></tr>
              ))}
              <tr className="font-bold"><td>Total Pax {r.seats_sold}</td><td /><td /><td className="text-right">{money(r.booking_amount)}</td></tr>
            </tbody>
          </table>
          <div className="text-xs">Platform {money(r.platform)} · Tracking {money(r.tracking)} · SM {money(r.sm_amount)} · Station {money(r.station_charge)} · Driver {money(r.driver_amount)}</div>
          <hr className="my-1.5 border-dashed border-slate-200" />
        </div>
      ))}
      <div className="font-bold">TOTALS — {totals.trips} trips · {totals.seats_sold} seats · {money(totals.booking_amount)}</div>
      <div className="text-xs">Platform {money(totals.platform)} · Tracking {money(totals.tracking)} · SM {money(totals.sm_amount)} · Station {money(totals.station_charge)} · Driver {money(totals.driver_amount)}</div>
      <div className="center mt-3 text-center text-[10px]">Printed {new Date().toLocaleString()}</div>
    </div>
  );
}

export default function StationTripReport() {
  const { user } = useAuth();
  const isSM = userRoles(user).includes("station_master") && !userRoles(user).some((r) => ["admin", "manager"].includes(r)) && !!user?.station_id;
  const stations = useStations();
  const [sid, setSid] = useState("");
  const effSid = isSM ? (user?.station_id || "") : sid;
  const [from, setFrom] = useState(daysAgo(7));
  const [to, setTo] = useState(daysAgo(0));
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);

  const load = useCallback(async () => {
    if (isSM && !effSid) return;
    setLoading(true);
    try {
      const { data: d } = await api.get("/station/trip-report", { params: { date_from: from, date_to: to, ...(effSid ? { station_id: effSid } : {}) } });
      setData(d);
    } catch (e) { toast.error(errMsg(e)); } finally { setLoading(false); }
  }, [from, to, effSid, isSM]);

  useEffect(() => { load(); }, [load]);

  const label = effSid ? (stations.find((s) => s.id === effSid) ? `${stations.find((s) => s.id === effSid).city} - ${stations.find((s) => s.id === effSid).name}` : "Station") : "All stations";
  const rows = data?.rows || [];
  const totals = data?.totals || {};

  return (
    <div className="space-y-6" data-testid="station-trip-report">
      <div>
        <h2 className="font-display text-2xl font-extrabold tracking-tight">Trip reports</h2>
        <p className="text-sm text-slate-500">Per-trip seats, revenue, charges and the From → To passenger split for a station and date range.</p>
      </div>

      <div className="flex flex-wrap items-end gap-3 rounded-2xl border border-slate-100 bg-slate-50/60 p-4">
        <div className="space-y-1">
          <Label className="text-xs font-semibold text-slate-600">Station</Label>
          {isSM ? (
            <Select value={effSid} disabled>
              <SelectTrigger className="w-56 bg-white" data-testid="trip-report-station-select"><SelectValue placeholder="Your station" /></SelectTrigger>
              <SelectContent>{stations.filter((s) => s.id === effSid).map((s) => <SelectItem key={s.id} value={s.id}>{s.city} - {s.name}</SelectItem>)}</SelectContent>
            </Select>
          ) : (
            <Select value={effSid || "all"} onValueChange={(v) => setSid(v === "all" ? "" : v)}>
              <SelectTrigger className="w-56 bg-white" data-testid="trip-report-station-select"><SelectValue placeholder="All stations" /></SelectTrigger>
              <SelectContent><SelectItem value="all">All stations</SelectItem>{stations.map((s) => <SelectItem key={s.id} value={s.id}>{s.city} - {s.name}</SelectItem>)}</SelectContent>
            </Select>
          )}
        </div>
        <label className="text-sm font-semibold text-slate-600">From<Input type="date" value={from} max={to} onChange={(e) => setFrom(e.target.value)} className="mt-1 w-40 bg-white" data-testid="trip-report-from" /></label>
        <label className="text-sm font-semibold text-slate-600">To<Input type="date" value={to} min={from} onChange={(e) => setTo(e.target.value)} className="mt-1 w-40 bg-white" data-testid="trip-report-to" /></label>
        <Button variant="outline" onClick={load} disabled={loading} data-testid="trip-report-refresh">{loading ? <Loader2 className="h-4 w-4 animate-spin" /> : "Refresh"}</Button>
        {rows.length > 0 && <PrintButtons testPrefix="trip-report-print">{(f) => <ReportDoc rows={rows} totals={totals} label={label} from={from} to={to} format={f} />}</PrintButtons>}
      </div>

      {loading ? <div className="grid h-40 place-items-center"><Loader2 className="h-6 w-6 animate-spin text-[#0052FF]" /></div> : !rows.length ? (
        <div className="card-soft grid h-60 place-items-center p-6 text-center text-slate-500" data-testid="trip-report-empty"><div><FileBarChart className="mx-auto h-10 w-10 text-blue-300" /><p className="mt-2 font-semibold">No paid trips in this period.</p></div></div>
      ) : (
        <>
          <div className="grid grid-cols-2 gap-3 lg:grid-cols-4" data-testid="trip-report-totals">
            <StatCard label="Trips" value={totals.trips} icon={BusFront} testid="trip-report-total-trips" />
            <StatCard label="Seats sold" value={totals.seats_sold} icon={FileBarChart} tint="bg-sky-50 text-sky-600" testid="trip-report-total-seats" />
            <StatCard label="Booking amount" value={money(totals.booking_amount)} icon={FileBarChart} tint="bg-emerald-50 text-emerald-600" testid="trip-report-total-amount" />
            <StatCard label="Station Master amount" value={money(totals.sm_amount)} icon={FileBarChart} tint="bg-amber-50 text-amber-600" testid="trip-report-total-sm" />
          </div>
          <div className="grid gap-4 xl:grid-cols-2">
            {rows.map((r) => <TripCard key={`${r.trip_id}-${r.station_id}`} r={r} />)}
          </div>
        </>
      )}
    </div>
  );
}
